import { FormattedMessage, defineMessages, useIntl } from 'react-intl'
import { ReactElement } from 'react'
import { useFormikContext } from 'formik'

import { Column } from 'components/v2/Column'
import FormLayout from 'components/v2/FormLayout'
import Heading from 'components/v2/Heading'
import { Input } from 'components/Input'
import { Label } from 'components/v2/Label'
import { Row } from 'components/v2/Row'
import { Select } from 'components/v2/Select'
import { SelectState } from 'features/Cities/components/SelectState'
import { INewDriverStep1 } from 'features/Drivers/types'
import { COUNTRY_CODES } from 'utils/countries'
import { safeFormatMessage } from 'utils/intl'
import { STATES } from 'utils/states'
import { PartnerAutocomplete } from 'features/Partners/components/PartnerAutocomplete'
import { GTBusinessPartnerAutocomplete } from 'gql/types.generated'

const messages = defineMessages({
  firstNamePlaceholder: {
    id: 'drivers.form.firstName.placeholder',
    defaultMessage: 'Enter the driver first name'
  },
  lastNamePlaceholder: {
    id: 'drivers.form.lastName.placeholder',
    defaultMessage: 'Enter the driver last name'
  },
  partnerPlaceholder: {
    id: 'drivers.form.partner.placeholder',
    defaultMessage: 'Select a partner'
  },
  phonePlaceholder: {
    id: 'drivers.form.phone.placeholder',
    defaultMessage: 'Enter phone'
  },
  emailPlaceholder: {
    id: 'drivers.form.email.placeholder',
    defaultMessage: 'Enter email'
  },
  statePlaceholder: {
    id: 'drivers.form.state.placeholder',
    defaultMessage: 'Select state'
  },
  genderPlaceholder: {
    id: 'drivers.form.gender.placeholder',
    defaultMessage: 'Select gender'
  }
})

const GENDER = {
  FEMALE: 'female',
  MALE: 'male',
  OTHER: 'other',
  NOT_SHARING: 'notSharing'
}

const genderMessages = defineMessages({
  [GENDER.FEMALE]: {
    id: 'gender.value.female',
    defaultMessage: 'Femenino'
  },
  [GENDER.MALE]: {
    id: 'gender.value.male',
    defaultMessage: 'Masculino'
  },
  [GENDER.OTHER]: {
    id: 'gender.value.other',
    defaultMessage: 'Otro'
  },
  [GENDER.NOT_SHARING]: {
    id: 'gender.value.notSharing',
    defaultMessage: 'No compartir'
  }
})

export const DriverPersonalInformationCard = (): ReactElement => {
  const intl = useIntl()
  const { values, handleChange, handleBlur, setFieldValue, touched, errors } = useFormikContext<INewDriverStep1>()

  const handleStateChange = ({ target: { value } }: React.ChangeEvent<HTMLSelectElement>): void => {
    const state = value && STATES.find((state) => state.code === value)

    if (state) {
      setFieldValue('state', state.code)
    } else {
      setFieldValue('state', null)
    }
  }

  return (
    <FormLayout.Card small>
      <Heading.H4>
        <FormattedMessage id="drivers.form.title" defaultMessage="Personal information" />
      </Heading.H4>
      <Row>
        <Column>
          <Input
            id="firstName"
            value={values.firstName}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder={intl.formatMessage(messages.firstNamePlaceholder)}
            label={<FormattedMessage id="drivers.form.firstName.label" defaultMessage="First name" />}
            touched={touched.firstName}
            errors={errors.firstName}
          />
        </Column>
        <Column>
          <Input
            id="lastName"
            value={values.lastName}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder={intl.formatMessage(messages.lastNamePlaceholder)}
            label={<FormattedMessage id="drivers.form.lastName.label" defaultMessage="Last name" />}
            touched={touched.lastName}
            errors={errors.lastName}
          />
        </Column>
      </Row>
      <Row>
        <Column>
          <PartnerAutocomplete
            label={<FormattedMessage id="drivers.form.partner.label" defaultMessage="Partner" />}
            placeholder={intl.formatMessage(messages.partnerPlaceholder)}
            value={values.partner.name}
            onChange={(partner: GTBusinessPartnerAutocomplete): void => {
              setFieldValue('partner', {
                id: partner.id,
                name: partner.name
              })
            }}
            touched={touched.partner?.id}
            errors={errors.partner?.id}
          />
        </Column>
        <Column>
          <Input
            id="phone"
            value={values.phone}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder={intl.formatMessage(messages.phonePlaceholder)}
            label={<FormattedMessage id="drivers.form.phone.label" defaultMessage="Phone" />}
            touched={touched.phone}
            errors={errors.phone}
          />
        </Column>
      </Row>
      <Row>
        <Column>
          <Input
            id="email"
            value={values.email}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder={intl.formatMessage(messages.emailPlaceholder)}
            label={<FormattedMessage id="drivers.form.email.label" defaultMessage="Email" />}
            touched={touched.email}
            errors={errors.email}
          />
        </Column>
        <Column>
          <Label optional>
            <FormattedMessage id="dictionary.state" defaultMessage="State" />
          </Label>
          <SelectState
            id="state"
            value={values?.state ?? ''}
            onChange={handleStateChange}
            onBlur={handleBlur}
            country={COUNTRY_CODES.MEXICO}
          />
        </Column>
      </Row>
      <Row>
        <Column width="50%">
          <Label optional>
            <FormattedMessage id="dictionary.gender" defaultMessage="Gender" />
          </Label>
          <Select
            id="gender"
            value={values.gender}
            onChange={handleChange}
            onBlur={handleBlur}
            touched={touched.gender}
            errors={errors.gender}
          >
            <option value="">{intl.formatMessage(messages.genderPlaceholder)}</option>
            {Object.entries(GENDER).map(([key, value]) => (
              <option key={key} value={value}>
                {safeFormatMessage({ intl, messages: genderMessages, key: value })}
              </option>
            ))}
          </Select>
        </Column>
      </Row>
    </FormLayout.Card>
  )
}
